import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  ExternalLink,
  Edit2,
  CheckCircle2,
  XCircle,
  Clock,
  PlayCircle,
  Beaker,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminBrandCatalogSync — /admin/brand-catalog-sync
 *
 * Companion to /admin/brand-discovery (which finds NEW brands).
 * This page handles the existing brand library — auto-imports each
 * brand's full public catalog with ingredient + allergen flagging.
 *
 *   Discovery → "we don't have this brand yet, vet it"
 *   Sync     → "we have these brands, pull EVERY product they ship"
 */

const PLATFORM_META = {
  shopify: {
    label: "Shopify",
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
  },
  sitemap: {
    label: "Sitemap + JSON-LD",
    color: "text-sky-300 bg-sky-500/10 border-sky-400/30",
  },
  playwright: {
    label: "Playwright (JS)",
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-400/30",
  },
  custom: {
    label: "Custom",
    color: "text-amber-200 bg-amber-500/10 border-amber-400/30",
  },
  unknown: {
    label: "Unknown",
    color: "text-white/55 bg-white/8 border-white/15",
  },
};

const STATUS_META = {
  ok: {
    label: "Synced",
    color: "text-emerald-300",
    Icon: CheckCircle2,
  },
  error: {
    label: "Error",
    color: "text-red-300",
    Icon: XCircle,
  },
  skipped: {
    label: "Skipped",
    color: "text-amber-200",
    Icon: AlertCircle,
  },
  null: {
    label: "Never synced",
    color: "text-white/45",
    Icon: Clock,
  },
};

const BrandCatalogSyncPage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingBrand, setSyncingBrand] = useState(null);
  const [globalSyncing, setGlobalSyncing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [probingBrand, setProbingBrand] = useState(null);

  const probePlaywright = async (brand) => {
    setProbingBrand(brand);
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-catalog/sources/${encodeURIComponent(brand)}/probe-playwright`,
        {},
        { withCredentials: true, timeout: 30000 },
      );
      if (data.promoted) {
        toast.success(`${brand} promoted to Playwright crawler`);
      } else {
        toast.error(
          `${brand} couldn't be unblocked (likely needs residential proxies)`,
        );
      }
      await loadSources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Probe failed");
    } finally {
      setProbingBrand(null);
    }
  };
  const [logs, setLogs] = useState([]);

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/brand-catalog/sources`,
        { withCredentials: true },
      );
      setSources(data.items || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't load brand sources",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/brand-catalog/logs?limit=10`,
        { withCredentials: true },
      );
      setLogs(data.items || []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadSources();
    loadLogs();
  }, [loadSources, loadLogs]);

  const runDiscover = async () => {
    setDiscovering(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-catalog/discover`,
        {},
        { withCredentials: true },
      );
      toast.success(
        `Sources refreshed · ${data.inserted} new, ${data.updated} updated`,
      );
      await loadSources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discover failed");
    } finally {
      setDiscovering(false);
    }
  };

  const syncOne = async (brand) => {
    setSyncingBrand(brand);
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-catalog/sources/${encodeURIComponent(brand)}/sync`,
        {},
        { withCredentials: true },
      );
      const status = data.status;
      if (status === "ok") {
        toast.success(
          `${brand} synced · ${data.products_total} products · ` +
            `${data.ingredient_coverage_pct ?? 0}% ingredients`,
        );
      } else if (status === "skipped") {
        toast.message(`${brand} skipped: ${data.error || "unknown reason"}`);
      } else {
        toast.error(`${brand} failed: ${data.error || "unknown error"}`);
      }
      await loadSources();
      await loadLogs();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Sync failed for ${brand}`,
      );
    } finally {
      setSyncingBrand(null);
    }
  };

  const syncAll = async () => {
    if (
      !window.confirm(
        "Sync ALL brands now? This will hit each brand's site and " +
          "may take 5-15 minutes for the full library.",
      )
    ) {
      return;
    }
    setGlobalSyncing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-catalog/sync-all`,
        {},
        { withCredentials: true, timeout: 15 * 60 * 1000 },
      );
      const stats = data.by_status || {};
      toast.success(
        `Sync complete · ${stats.ok || 0} ok · ${
          stats.skipped || 0
        } skipped · ${stats.error || 0} errors`,
      );
      await loadSources();
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk sync failed");
    } finally {
      setGlobalSyncing(false);
    }
  };

  const startEdit = (source) => {
    setEditingBrand(source.brand);
    setEditUrl(source.website_url || "");
  };

  const saveEdit = async () => {
    if (!editUrl.trim()) return;
    try {
      await axios.post(
        `${API}/admin/brand-catalog/sources/${encodeURIComponent(
          editingBrand,
        )}/url`,
        { website_url: editUrl.trim() },
        { withCredentials: true },
      );
      toast.success(`Updated ${editingBrand}`);
      setEditingBrand(null);
      setEditUrl("");
      await loadSources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // Top-line stats
  const totals = sources.reduce(
    (acc, s) => {
      acc.products += s.products_imported || 0;
      if (s.platform === "shopify") acc.shopify += 1;
      if (s.platform === "sitemap") acc.sitemap += 1;
      if (s.needs_review) acc.review += 1;
      if (s.last_sync_status === "ok") acc.synced += 1;
      return acc;
    },
    { products: 0, shopify: 0, sitemap: 0, review: 0, synced: 0 },
  );

  return (
    <AdminShell
      title="Brand catalog sync"
      eyebrow="Ingestion"
      subtitle="Auto-import every public product from each brand in your library, with ingredient + allergen flagging. Trouvé recommends from the ingested catalog alongside your curated picks."
    >
      <div className="max-w-6xl space-y-8">
        {/* ─── Top-line stats + global controls ─── */}
        <section className="bg-[#1A1720] border border-white/8 rounded-2xl p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4B483] to-[#9B6A6C] flex items-center justify-center shrink-0">
                <Beaker className="h-5 w-5 text-[#1A1720]" />
              </div>
              <div>
                <h2 className="text-white text-lg font-serif italic">
                  Library coverage
                </h2>
                <p className="text-white/55 text-sm leading-relaxed">
                  Public Shopify <code className="text-[#D4B483]">/products.json</code>{" "}
                  endpoints — no paid APIs. Custom platforms (Sephora-exclusive,
                  Salesforce Commerce) flagged for Phase 2.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={runDiscover}
                disabled={discovering}
                data-testid="brand-catalog-discover-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 disabled:opacity-30 text-white/75 rounded-full text-xs font-medium transition"
              >
                {discovering ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Re-detect sources
              </button>
              <button
                onClick={syncAll}
                disabled={globalSyncing}
                data-testid="brand-catalog-sync-all-btn"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#D4B483] hover:bg-[#E0C295] disabled:opacity-30 text-[#1A1720] rounded-full text-xs font-medium transition"
              >
                {globalSyncing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Syncing all…
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-3.5 w-3.5" />
                    Sync all
                  </>
                )}
              </button>
            </div>
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-6 gap-3"
            data-testid="brand-catalog-stats"
          >
            <Stat label="Brands" value={sources.length} />
            <Stat label="Shopify" value={totals.shopify} />
            <Stat label="Sitemap+JSON-LD" value={totals.sitemap} />
            <Stat label="Playwright" value={totals.playwright} />
            <Stat label="Products ingested" value={totals.products} accent />
            <Stat label="Needs review" value={totals.review} alert={totals.review > 0} />
          </div>
        </section>

        {/* ─── Brand table ─── */}
        <section>
          <h2 className="text-white text-lg font-serif italic mb-4">
            Brands
          </h2>
          {loading ? (
            <div className="flex justify-center py-12 text-white/40">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12 text-white/40 text-sm bg-[#1A1720]/50 border border-white/5 rounded-2xl">
              No brand sources yet. Hit "Re-detect sources" to populate from
              your existing product library.
            </div>
          ) : (
            <div
              className="space-y-2"
              data-testid="brand-catalog-source-list"
            >
              {sources.map((s) => (
                <SourceRow
                  key={s.brand}
                  source={s}
                  busy={syncingBrand === s.brand}
                  probing={probingBrand === s.brand}
                  editing={editingBrand === s.brand}
                  editUrl={editUrl}
                  setEditUrl={setEditUrl}
                  onStartEdit={() => startEdit(s)}
                  onCancelEdit={() => {
                    setEditingBrand(null);
                    setEditUrl("");
                  }}
                  onSaveEdit={saveEdit}
                  onSync={() => syncOne(s.brand)}
                  onProbePlaywright={() => probePlaywright(s.brand)}
                />
              ))}
            </div>
          )}
        </section>

        {/* ─── Recent sync logs ─── */}
        {logs.length > 0 && (
          <section className="mt-12 pt-6 border-t border-white/5">
            <h3 className="text-white/55 text-xs uppercase tracking-[0.18em] mb-3">
              Recent syncs
            </h3>
            <div className="space-y-2">
              {logs.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between bg-[#1A1720]/60 border border-white/5 rounded-xl px-4 py-2.5 text-xs text-white/60"
                  data-testid="brand-catalog-log-row"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Clock className="h-3.5 w-3.5 text-white/30 shrink-0" />
                    <span className="shrink-0">
                      {new Date(l.started_at).toLocaleString()}
                    </span>
                    <span className="text-white/85 truncate">{l.brand}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className={STATUS_META[l.status]?.color || "text-white/40"}>
                      {STATUS_META[l.status]?.label || l.status}
                    </span>
                    {l.products_total != null && (
                      <span>{l.products_total} products</span>
                    )}
                    {l.ingredient_coverage_pct != null && (
                      <span>{l.ingredient_coverage_pct}% INCI</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AdminShell>
  );
};

const Stat = ({ label, value, accent, alert }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
      {label}
    </div>
    <div
      className={`text-2xl font-light mt-0.5 ${
        alert ? "text-amber-200" : accent ? "text-[#D4B483]" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const SourceRow = ({
  source,
  busy,
  probing,
  editing,
  editUrl,
  setEditUrl,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onSync,
  onProbePlaywright,
}) => {
  const platform = PLATFORM_META[source.platform] || PLATFORM_META.unknown;
  const statusKey = source.last_sync_status ?? "null";
  const status = STATUS_META[statusKey];
  const StatusIcon = status?.Icon || Clock;
  const lastSync = source.last_sync_at
    ? new Date(source.last_sync_at).toLocaleDateString()
    : null;

  return (
    <article
      className="bg-[#1A1720] border border-white/8 rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center"
      data-testid="brand-catalog-source-row"
      data-brand={source.brand}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-white text-sm font-medium">
            {source.brand}
          </span>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${platform.color}`}
          >
            {platform.label}
          </span>
          {source.needs_review && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-200 bg-amber-500/10 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Needs URL
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-wider ml-1 inline-flex items-center gap-1 ${status.color}`}
          >
            <StatusIcon className="h-3 w-3" />
            {status.label}
            {lastSync && (
              <span className="text-white/40 ml-1">· {lastSync}</span>
            )}
          </span>
        </div>
        {editing ? (
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="https://brand.com/"
              className="flex-1 bg-[#0F0D14] border border-white/10 rounded px-2 py-1 text-xs text-white/85 focus:outline-none focus:border-[#D4B483]/40"
              data-testid="brand-catalog-url-input"
            />
            <button
              onClick={onSaveEdit}
              className="text-xs px-2 py-1 bg-[#D4B483] text-[#1A1720] rounded font-medium"
              data-testid="brand-catalog-url-save-btn"
            >
              Save
            </button>
            <button
              onClick={onCancelEdit}
              className="text-xs px-2 py-1 text-white/55 hover:text-white/85"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-0.5">
            {source.website_url ? (
              <a
                href={source.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-white/45 hover:text-[#D4B483] transition inline-flex items-center gap-1 truncate"
              >
                {source.website_url}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
            ) : (
              <span className="text-[11px] text-white/35 italic">
                No URL on file
              </span>
            )}
            <button
              onClick={onStartEdit}
              className="text-white/40 hover:text-white/85 transition"
              title="Edit URL"
              data-testid="brand-catalog-edit-url-btn"
            >
              <Edit2 className="h-3 w-3" />
            </button>
          </div>
        )}
        {source.products_imported > 0 && (
          <div className="flex items-center gap-4 mt-2 text-[11px] text-white/55">
            <span>
              <span className="text-white">{source.products_imported}</span> products
            </span>
            {source.ingredient_coverage_pct != null && (
              <span>
                <span className="text-white">{source.ingredient_coverage_pct}%</span> INCI
              </span>
            )}
            {source.allergen_coverage_pct != null && (
              <span>
                <span className="text-white">{source.allergen_coverage_pct}%</span> allergens
              </span>
            )}
          </div>
        )}
        {source.last_error && (
          <div className="text-[11px] text-red-300 mt-1.5 max-w-2xl">
            {source.last_error}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {(source.platform === "custom" || source.platform === "unknown") && source.website_url && (
          <button
            onClick={onProbePlaywright}
            disabled={probing || busy}
            data-testid="brand-catalog-probe-playwright-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 disabled:opacity-30 disabled:cursor-not-allowed text-fuchsia-300 rounded-full text-xs font-medium transition border border-fuchsia-400/20"
            title="Try crawling this brand with a headless browser (Phase 3)"
          >
            {probing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Probing…
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                Try Playwright
              </>
            )}
          </button>
        )}
        <button
          onClick={onSync}
          disabled={busy || !source.website_url}
          data-testid="brand-catalog-sync-one-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 disabled:opacity-30 disabled:cursor-not-allowed text-emerald-300 rounded-full text-xs font-medium transition"
        >
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Syncing…
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5" />
              Sync now
            </>
          )}
        </button>
      </div>
    </article>
  );
};

export default BrandCatalogSyncPage;
